<template>
  <b-field
    :horizontal="horizontal"
    :class="{ 'show-as-label': showAsInputOnFocusOnly, 'read-only-field': showReadOnlyTooltip }"
    :type="fieldType"
    :title="fieldMessage"
    data-cy="input-field"
  >
    <template v-if="showLabel" #label>
      <!-- Slot for overriding label template -->
      <slot name="label">
        <b-tooltip
          :active="isTooltipActive"
          :label="formattedTooltip"
          type="is-dark"
          :multilined="true"
          :dashed="isTooltipActive"
          :append-to-body="appendToBody"
          data-cy="input-tooltip"
        >
          {{ formattedLabel }}
          <span v-if="showRequiredAsterisk" v-once>*</span>
        </b-tooltip>
      </slot>
      <p class="sub-label" data-cy="sub-label">
        <slot name="sub-label" />
      </p>
    </template>
    <ValidationProvider
      :rules="rules"
      v-slot="validationProps"
      :name="formattedLabel"
      :vid="computedName"
      :mode="ruleMode"
      :debounce="validateAfter"
      :immediate="validateImmediately"
    >
      <b-input
        v-if="!isPhoneNumberInput && !isCurrencyInput && !isSuggestionsEnabled"
        v-model="computedValue"
        :class="getClasses(validationProps)"
        :type="type"
        :icon="icon"
        :rows="rows"
        :icon-pack="iconPack"
        :max="max"
        :min="min"
        :minlength="minLength"
        :maxlength="maxLength"
        :autocomplete="autocomplete"
        :disabled="readOnly && !isReadOnlyScrollableTextarea"
        :readonly="isReadOnlyScrollableTextarea"
        ref="refInput"
        :step="step"
        :placeholder="computedPlaceholder"
        :use-html5-validation="useHtml5Validation"
        :password-reveal="passwordReveal"
        :data-cy="cypressLocator"
        @keyup.native.enter="overideOnEnter"
        @keyup.native="onKeyUp"
        @keypress.native="filterInputValue"
        @blur="onBlur"
      />

      <!-- Enhanced textarea with suggestions -->
      <div
        v-else-if="!isPhoneNumberInput && !isCurrencyInput && isSuggestionsEnabled"
        class="forminput-control-wrap has-footer"
      >
        <b-input
          v-model="computedValue"
          :class="getClasses(validationProps)"
          :type="type"
          :icon="icon"
          :rows="rows"
          :icon-pack="iconPack"
          :max="max"
          :min="min"
          :minlength="minLength"
          :maxlength="maxLength"
          :disabled="readOnly"
          ref="refInput"
          :step="step"
          :placeholder="computedPlaceholder"
          :use-html5-validation="useHtml5Validation"
          :password-reveal="passwordReveal"
          :data-cy="cypressLocator"
          @keyup.native.enter="overideOnEnter"
          @keyup.native="onKeyUp"
          @keypress.native="filterInputValue"
        />

        <div class="forminput-footer" data-cy="suggestions-footer" ref="suggestionsFooter">
          <a
            class="forminput-footer-link"
            data-cy="suggestions-dropdown-link"
            @mousedown.prevent
            @click="toggleSuggestions"
          >
            <b-icon pack="ti" custom-class="ti-sparkle" class="forminput-footer-icon" />
            <span class="forminput-footer-text">{{ $t('Suggestions') }}</span>
            <b-icon
              pack="ti"
              :custom-class="isSuggestionsOpen ? 'ti-caret-right' : 'ti-caret-down'"
              class="forminput-footer-caret"
            />
          </a>
        </div>

        <div
          v-if="isSuggestionsOpen && filteredSuggestions.length"
          class="forminput-suggestions"
          data-cy="suggestions-dropdown"
          ref="suggestionsDropdown"
          @mousedown.prevent
        >
          <div
            v-for="item in filteredSuggestions"
            :key="item.key"
            class="forminput-suggestion-item"
            data-cy="suggestion-item"
            @mousedown.prevent
            @click="onSuggestionSelect(item.value)"
          >
            {{ item.key }}
          </div>
        </div>
      </div>
      <div v-else-if="isCurrencyInput" class="control is-clearfix">
        <CurrencyInput
          v-model.lazy="computedValue"
          :class="getClasses(validationProps)"
          :disabled="readOnly"
          ref="refInput"
          :placeholder="computedPlaceholder"
          :options="computedCurrencyOptions"
          class="input"
          :data-cy="cypressLocator"
          @keyup.native.enter="onEnter"
          @keyup.native="onKeyUp"
          @blur="onBlur"
        />
      </div>
      <VueTelInput
        v-else
        v-model="computedValue"
        ref="refInput"
        :dynamic-placeholder="true"
        mode="international"
        :valid-characters-only="true"
        :preferred-countries="phonePreferredCountries"
        :style-classes="phoneWrapperClasses"
        :input-options="computedPhoneInputOptions"
        :data-cy="cypressLocator"
        @keyup.native.enter="onEnter"
        @keyup.native="onKeyUp"
        @blur="onBlur"
      >
        <template #arrow-icon="{ open }">
          <span class="vti__dropdown-arrow">{{ open ? '▲' : '▼' }}</span>
        </template>
      </VueTelInput>
      <slot name="error" v-bind="validationProps">
        <template v-if="hasValidationErrors(validationProps)">
          <span
            class="has-text-danger is-size-7 is-flex-baseline is-absolute validation-errors show-with-background"
            data-cy="validation-errors"
          >
            <b-icon
              pack="ti"
              custom-class="ti-alert-circle"
              size="is-small"
              class="icon-button no-hover-effect"
              data-cy="close-icon"
            />
            {{ validationProps.errors.join(', ') }}
          </span>
        </template>
      </slot>
    </ValidationProvider>
    <b-tooltip
      class="readonly-tooltip"
      v-if="showReadOnlyTooltip"
      :label="readOnlyTooltip"
      :position="readOnlyTooltipPosition"
      type="is-dark"
      :multilined="true"
      :dashed="false"
      data-cy="input-tooltip"
    />
  </b-field>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import { FormInputMixin } from '@/modules/core/form/mixins/FormInputMixin';
import CurrencyInput from '@/modules/core/form/components/CurrencyInput';
import { FormCypressLocatorMixin } from '@/modules/core/form/mixins/FormCypressLocatorMixin';
import { FORM_INPUT_TYPE } from '@/modules/core/form/form.constants';

const VueTelInput = () =>
  Promise.all([
    import(/* webpackChunkName: "vue-tel-input" */ 'vue-tel-input'),
    import(/* webpackChunkName: "vue-tel-input" */ 'vue-tel-input/dist/vue-tel-input.css'),
  ]).then(([{ VueTelInput: VueTelInputComponent }]) => VueTelInputComponent);

export default {
  mixins: [FormCypressLocatorMixin, FormInputMixin],
  components: { ValidationProvider, VueTelInput, CurrencyInput },
  props: {
    type: { type: String, default: 'text' },
    icon: { type: String, default: '' },
    iconPack: { type: String, default: 'fa' },
    max: { type: [Number, String], default: undefined },
    min: { type: [Number, String], default: undefined },
    step: { type: [Number, String], default: undefined },
    isInteger: { type: Boolean, default: undefined },
    rows: { type: Number, default: undefined },
    // Render a read-only textarea with the native `readonly` attribute instead
    // of `disabled` so its content stays scrollable/selectable. Opt-in; default
    // behavior (disabled) is unchanged for every other usage.
    readOnlyScrollable: { type: Boolean, default: false },
    currencyOptions: { type: Object, default: undefined },
    phoneInputOptions: { type: Object, default: undefined },
    fieldType: { type: String, default: undefined },
    fieldMessage: { type: String, default: undefined },
    customClasses: { type: String, default: undefined },
    useHtml5Validation: {
      type: Boolean,
      default: true,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    passwordReveal: {
      type: Boolean,
      default: false,
    },
    overideDefaultOnEnter: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: undefined,
    },
    enableSuggestions: { type: Boolean, default: false },
    suggestions: {
      type: [Array, Object],
      default: () => ({}),
    },
  },
  static: {
    // These countries will appear on the top of the list
    phonePreferredCountries: ['US', 'CA', 'GB'],
    phoneWrapperClasses: ['control', 'is-clearfix'],
    phoneInputClasses: ['input'],
    defaultCurrencyOptions: {
      locale: 'en',
      currency: 'USD',
      currencyDisplay: 'narrowSymbol',
      hideCurrencySymbolOnFocus: false,
      hideGroupingSeparatorOnFocus: true,
      hideNegligibleDecimalDigitsOnFocus: true,
      autoDecimalDigits: false,
      useGrouping: true,
      accountingSign: false,
      valueAsInteger: true,
      precision: 0,
      valueRange: { min: 0 },
    },
  },
  data() {
    return {
      defaultPhoneInputOptions: {
        autocomplete: 'on',
        maxlength: 25,
        styleClasses: ['input'],
        placeholder: i18n.$t('Enter a phone number'),
        type: 'tel',
      },
      isSuggestionsOpen: false,
    };
  },
  computed: {
    showReadOnlyTooltip() {
      return !!(this.readOnly && this.readOnlyTooltip);
    },
    defaultCYLocator() {
      if (!this.isPhoneNumberInput && !this.isCurrencyInput) {
        return 'input-element';
      }
      if (this.isCurrencyInput) {
        return 'currency-element';
      }

      return 'tele-input';
    },
    computedPlaceholder() {
      return this.placeholder || this.$t(`Enter {label}`, { label: this.formattedLabel });
    },
    isPhoneNumberInput() {
      return ['phone', 'tel'].includes(this.type);
    },
    isCurrencyInput() {
      return this.type === 'currency';
    },
    computedCurrencyOptions() {
      return {
        ...this.defaultCurrencyOptions,
        ...(this.currencyOptions || {}),
      };
    },
    computedPhoneInputOptions() {
      return {
        ...this.defaultPhoneInputOptions,
        ...(this.phoneInputOptions || {}),
      };
    },
    isTextarea() {
      return this.type === FORM_INPUT_TYPE.TEXTAREA;
    },
    isReadOnlyScrollableTextarea() {
      return this.readOnly && this.readOnlyScrollable && this.isTextarea;
    },
    isSuggestionsEnabled() {
      if (!this.enableSuggestions || !this.isTextarea || this.readOnly) {
        return false;
      }

      if (Array.isArray(this.suggestions)) {
        return this.suggestions.length > 0;
      }

      // eslint-disable-next-line tap/no-raw-text-js
      if (this.suggestions && typeof this.suggestions === 'object') {
        return Object.keys(this.suggestions).length > 0;
      }

      return false;
    },
    filteredSuggestions() {
      if (Array.isArray(this.suggestions)) {
        return this.suggestions.map((v) => ({
          key: v,
          value: v,
        }));
      }

      // eslint-disable-next-line tap/no-raw-text-js
      if (this.suggestions && typeof this.suggestions === 'object') {
        return Object.entries(this.suggestions).map(([key, value]) => ({
          key,
          value,
        }));
      }

      return [];
    },
  },
  mounted() {
    if (this.isSuggestionsEnabled) {
      document.addEventListener('click', this.handleOutsideClick);
    }
  },
  beforeDestroy() {
    if (this.isSuggestionsEnabled) {
      document.removeEventListener('click', this.handleOutsideClick);
    }
  },
  methods: {
    setFocus() {
      this.$refs.refInput?.$refs.input?.focus();
    },
    filterInputValue(event) {
      // Restrict decimal number if isInteger provided as true
      if (this.type === 'number' && this.isInteger && event.keyCode === 46) {
        event.preventDefault();
      }
    },
    overideOnEnter() {
      if (!this.overideDefaultOnEnter) {
        this.onEnter();
      }
    },
    toggleSuggestions() {
      this.isSuggestionsOpen = !this.isSuggestionsOpen;
    },
    onSuggestionSelect(text) {
      this.computedValue = text;
      this.isSuggestionsOpen = false;
      this.$emit('suggestion-select', text);
    },
    handleOutsideClick(event) {
      if (!this.isSuggestionsOpen) {
        return;
      }

      const footer = this.$refs.suggestionsFooter;
      const dropdown = this.$refs.suggestionsDropdown;

      const isClickInsideFooter = footer && footer.contains(event.target);
      const isClickInsideDropdown = dropdown && dropdown.contains(event.target);

      if (!isClickInsideFooter && !isClickInsideDropdown) {
        this.isSuggestionsOpen = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
:deep() {
  textarea,
  textarea:disabled {
    border-radius: rem(5px);
    border: 1px solid $grey-lighter;
  }
  .control.has-icons-left {
    .icon {
      color: $grey;
    }
  }
  .control.has-icons-right .icon.is-right {
    i:hover {
      color: $primary-light-10;
    }
    .fa-eye-slash::before,
    .fa-eye::before {
      font-size: 0.75rem;
      font-weight: 700;
      font-family: 'Mulish';
      padding-right: rem(12px);
    }
    .fa-eye-slash::before {
      content: var(--text-hide, 'Hide');
    }
    .fa-eye::before {
      content: var(--text-show, 'Show');
    }
    height: rem(40px);
  }
}
.vue-tel-input {
  border-radius: rem(5px);
  background-color: $background-color;
  border-color: $grey-lighter;
  color: #363636;

  &:hover {
    border-color: $grey-light;
  }

  &:focus-within {
    box-shadow: none;
    border-color: $grey-light;
  }

  :deep() {
    .vti__input {
      border-radius: rem(5px);
    }

    .vti__dropdown {
      border-top-left-radius: rem(5px);
      border-bottom-left-radius: rem(5px);

      &:focus {
        outline: none;
      }
    }

    .vti__dropdown-list {
      &.below {
        top: rem(32px);
        border-bottom-left-radius: rem(5px);
        border-bottom-right-radius: rem(5px);
      }
      &.top {
        border-top-left-radius: rem(5px);
        border-top-right-radius: rem(5px);
      }
    }

    .vti__dropdown-item {
      padding: rem(6px);

      strong {
        font-weight: 400;
      }
    }
  }
}

.is-horizontal {
  :deep() {
    .field-label {
      text-align: left;
      width: 35%;
      flex-basis: unset;

      .label {
        font-size: rem(14px);
        margin-top: rem(5px);
      }
      .sub-label {
        font-size: rem(12px);
        line-height: 1;
        font-weight: normal;
      }
    }
    .field-body {
      width: 65%;
      flex-basis: unset;
    }
  }
}

div.field.read-only-field {
  position: relative;

  .readonly-tooltip {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    &.is-right {
      :deep() {
        .tooltip-content {
          left: 50%;
        }
      }
    }
  }
}

.show-as-label {
  :deep() {
    .field-body {
      &:not(:hover) {
        .input,
        .vue-tel-input {
          border: rem(1px) solid transparent;
        }
      }

      input:disabled,
      textarea:disabled {
        border: rem(1px) solid transparent;
        background-color: transparent;
        color: $text;
        cursor: default;
      }
    }
  }
}
.show-with-background {
  background-color: $background-color;
  margin-top: rem(3px);
  .icon {
    height: rem(18px);
  }
}
:deep(.icon-button.no-hover-effect:hover) {
  background-color: transparent;
  color: inherit;
  cursor: default;
}

.forminput-control-wrap {
  position: relative;
  width: 100%;
}

.forminput-footer {
  display: flex;
  align-items: center;
  padding: rem(6px) rem(10px);
  background: transparent;
  border: 0;
}

.forminput-footer-link {
  display: inline-flex;
  align-items: center;
  gap: rem(6px);
  color: $primary;
  font-weight: 600;
  font-size: rem(13px);
  text-decoration: none;
  cursor: pointer;
  opacity: 0.85;

  &:hover {
    text-decoration: none;
    opacity: 1;
  }
}

.forminput-footer-icon :deep(i) {
  font-size: rem(18px);
}

.forminput-footer-caret :deep(i) {
  font-size: rem(8px);
}

.forminput-suggestions {
  position: relative;
  left: auto;
  right: auto;
  bottom: auto;
  margin-top: rem(6px);
  z-index: 60;
  background: #fff;
  border: 1px solid $grey-lighter;
  border-radius: rem(10px);
  box-shadow: 0 rem(10px) rem(26px) rgba(0, 0, 0, 0.14);
  overflow: hidden;
  max-height: rem(260px);
  overflow-y: auto;
}

.forminput-suggestions {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(#{rem(10px)} + #{rem(24px)} + #{rem(8px)});
  z-index: 60;
  background: #fff;
  border: 1px solid $grey-lighter;
  border-radius: rem(10px);
  box-shadow: 0 rem(10px) rem(26px) rgba(0, 0, 0, 0.14);
  overflow: hidden;
  max-height: rem(260px);
  overflow-y: auto;
}

.forminput-suggestion-item {
  padding: rem(14px) rem(16px);
  cursor: pointer;
  font-size: rem(14px);
  color: $text;

  &:hover {
    background: $background-color;
  }

  & + & {
    border-top: 1px solid $grey-lighter;
  }
}
</style>
